import { publicActions, type Abi, type Address, type Hex, type WalletClient, ContractFunctionRevertedError } from 'viem';

import { config } from '../config.ts';

/**
 * Reads for a wallet transaction go through the wallet's own provider (its RPC, not ours): the app
 * itself only talks to the API.
 */
export const walletReader = (wallet: WalletClient) => wallet.extend(publicActions);

/** Simulates (to surface the revert reason before the wallet prompt), sends and waits for inclusion. */
export async function sendTx(
  wallet: WalletClient,
  c: { address: Address; abi: Abi | readonly unknown[]; functionName: string; args: unknown[]; value?: bigint },
): Promise<Hex> {
  const account = wallet.account!;
  const publicClient = walletReader(wallet);
  const { request } = await publicClient.simulateContract({ account, ...c } as never);
  const hash = await wallet.writeContract({ ...(request as object), account, chain: config.chain } as never);
  const r = await publicClient.waitForTransactionReceipt({ hash });
  if (r.status !== 'success') throw new Error(`${c.functionName} reverted (${hash})`);
  return hash;
}

const PRICE_MOVED = 'The price moved past your max slippage before the order ran. Try again or raise it.';

/** Readable message from a viem / wallet / relayer error */
export function errorMessage(e: unknown): string {
  const err = e as { shortMessage?: string; message?: string; code?: number; walk?: (fn: (x: unknown) => boolean) => unknown };
  if (err?.code === 4001) return 'Rejected in the wallet';
  // a revert: viem keeps a custom error's name (and args) in the cause, a string reason on the message's second line
  const reverted = err?.walk?.((x) => x instanceof ContractFunctionRevertedError) as ContractFunctionRevertedError | undefined;
  if (reverted) {
    const name = reverted.data?.errorName ?? reverted.reason;
    if (name === 'PriceLimitExceeded') return PRICE_MOVED;
    if (name) {
      const args = reverted.data?.args?.length ? `(${reverted.data.args.map(String).join(', ')})` : '';
      return `Reverted: ${name}${args}`.slice(0, 200);
    }
  }
  const m = err?.shortMessage ?? err?.message ?? String(e);
  if (m.includes('PriceLimitExceeded')) return PRICE_MOVED;
  const lines = m.split('\n').map((l) => l.trim()).filter(Boolean);
  const text = lines.length > 1 && /reverted with the following reason/.test(lines[0]!) ? `Reverted: ${lines[1]}` : (lines[0] ?? String(e));
  return text.slice(0, 200);
}
